import {
  ANIMATABLE_PROPERTIES,
  type AnimatableProperty,
  type BehaviorConnection,
  type BehaviorNode,
  type Port,
} from "./types.js";

/** Flow + timeline offset ports that every keyframe must keep. */
export const KEYFRAME_PROTECTED_PORT_IDS = new Set([
  "in",
  "out",
  "offset",
  "offsetOut",
]);

/** Optional meta port (addable / removable) for per-stop easing. */
export const KEYFRAME_EASING_PORT_ID = "easing";
export const KEYFRAME_EASING_OUT_PORT_ID = "easingOut";

export type KeyframeAddablePortId =
  | typeof KEYFRAME_EASING_PORT_ID
  | AnimatableProperty;

export type AnimKeyframePortLabels = {
  flowIn: string;
  flowOut: string;
  offset: string;
  offsetOut: string;
  easing: string;
  easingOut: string;
  mirrorSuffix: string;
  props: Record<AnimatableProperty, string>;
};

/** Default literal values when a property port is added without a capture snapshot. */
export const DEFAULT_ANIMATABLE_VALUES: Record<AnimatableProperty, number> = {
  opacity: 1,
  x: 0,
  y: 0,
  scale: 1,
  scaleX: 1,
  scaleY: 1,
  rotation: 0,
  skewX: 0,
  skewY: 0,
  width: 0,
  height: 0,
};

/**
 * Returns whether a port id is protected (flow or mandatory Position/offset).
 */
export function isProtectedKeyframePortId(portId: string): boolean {
  return KEYFRAME_PROTECTED_PORT_IDS.has(portId);
}

/**
 * Lists animatable property ids that currently have an input port on the node.
 */
export function getAnimKeyframePropertyIds(
  node: BehaviorNode,
): AnimatableProperty[] {
  if (node.type !== "action" || node.kind !== "animKeyframe") return [];
  const present = new Set(
    node.ports
      .filter((port) => port.direction === "input")
      .map((port) => port.id),
  );
  return ANIMATABLE_PROPERTIES.filter((prop) => present.has(prop));
}

/**
 * True when the keyframe exposes an easing input port.
 */
export function keyframeHasEasingPort(node: BehaviorNode): boolean {
  if (node.type !== "action" || node.kind !== "animKeyframe") return false;
  return node.ports.some(
    (port) =>
      port.id === KEYFRAME_EASING_PORT_ID && port.direction === "input",
  );
}

/**
 * Ports that can still be added to a keyframe (easing + missing animatable props).
 */
export function getAvailableKeyframePortIds(
  node: BehaviorNode,
): KeyframeAddablePortId[] {
  if (node.type !== "action" || node.kind !== "animKeyframe") return [];
  const present = new Set(
    node.ports
      .filter((port) => port.direction === "input")
      .map((port) => port.id),
  );
  const available: KeyframeAddablePortId[] = [];
  if (!present.has(KEYFRAME_EASING_PORT_ID)) {
    available.push(KEYFRAME_EASING_PORT_ID);
  }
  for (const prop of ANIMATABLE_PROPERTIES) {
    if (!present.has(prop)) available.push(prop);
  }
  return available;
}

/**
 * Resolves a display label for an addable keyframe port id.
 */
export function getKeyframeAddablePortLabel(
  portId: KeyframeAddablePortId,
  labels: AnimKeyframePortLabels,
): string {
  if (portId === KEYFRAME_EASING_PORT_ID) return labels.easing;
  return labels.props[portId];
}

function createMirrorPair(
  id: string,
  label: string,
  valueType: Port["valueType"],
  value: Port["value"] | undefined,
  mirrorSuffix: string,
): Port[] {
  return [
    {
      id,
      label,
      direction: "input",
      valueType,
      value,
    },
    {
      id: `${id}Out`,
      label: `${label} ${mirrorSuffix}`,
      direction: "output",
      valueType,
    },
  ];
}

/**
 * Builds the full port list for an `animKeyframe` node.
 *
 * Always includes flow + offset (Position). Optional easing and animatable
 * properties are included only when listed in `options`.
 */
export function buildAnimKeyframePorts(
  labels: AnimKeyframePortLabels,
  options: {
    offset?: number;
    easing?: string;
    includeEasing?: boolean;
    propertyValues?: Partial<Record<AnimatableProperty, number>>;
    properties?: readonly AnimatableProperty[];
  } = {},
): Port[] {
  const includeEasing = options.includeEasing !== false;
  const properties = options.properties ?? [];
  const propertyValues = options.propertyValues ?? {};

  const ports: Port[] = [
    {
      id: "in",
      label: labels.flowIn,
      direction: "input",
      valueType: "flow",
    },
    {
      id: "out",
      label: labels.flowOut,
      direction: "output",
      valueType: "flow",
    },
    ...createMirrorPair(
      "offset",
      labels.offset,
      "number",
      options.offset ?? 0,
      labels.mirrorSuffix,
    ),
  ];

  if (includeEasing) {
    ports.push(
      ...createMirrorPair(
        KEYFRAME_EASING_PORT_ID,
        labels.easing,
        "animationEasing",
        options.easing ?? "ease",
        labels.mirrorSuffix,
      ),
    );
  }

  for (const prop of properties) {
    const value =
      propertyValues[prop] ?? DEFAULT_ANIMATABLE_VALUES[prop];
    ports.push(
      ...createMirrorPair(
        prop,
        labels.props[prop],
        "number",
        value,
        labels.mirrorSuffix,
      ),
    );
  }

  return ports;
}

/**
 * Adds a property (or easing) port pair to every `animKeyframe` in the graph
 * and returns the updated nodes + connections (unchanged).
 */
export function addAnimKeyframePortAcrossGraph(
  nodes: BehaviorNode[],
  connections: BehaviorConnection[],
  portId: KeyframeAddablePortId,
  labels: AnimKeyframePortLabels,
  defaultValue?: number | string,
): { nodes: BehaviorNode[]; connections: BehaviorConnection[] } {
  const nextNodes = nodes.map((node) => {
    if (node.type !== "action" || node.kind !== "animKeyframe") return node;
    if (node.ports.some((port) => port.id === portId)) return node;

    let extraPorts: Port[];
    if (portId === KEYFRAME_EASING_PORT_ID) {
      extraPorts = createMirrorPair(
        KEYFRAME_EASING_PORT_ID,
        labels.easing,
        "animationEasing",
        typeof defaultValue === "string" ? defaultValue : "ease",
        labels.mirrorSuffix,
      );
    } else {
      const numericDefault =
        typeof defaultValue === "number"
          ? defaultValue
          : DEFAULT_ANIMATABLE_VALUES[portId];
      extraPorts = createMirrorPair(
        portId,
        labels.props[portId],
        "number",
        numericDefault,
        labels.mirrorSuffix,
      );
    }

    return {
      ...node,
      ports: [...node.ports, ...extraPorts],
    };
  });

  return { nodes: nextNodes, connections };
}

/**
 * Removes a property (or easing) port pair from every `animKeyframe` and strips
 * connections that referenced those ports. Offset/flow ports cannot be removed.
 */
export function removeAnimKeyframePortAcrossGraph(
  nodes: BehaviorNode[],
  connections: BehaviorConnection[],
  portId: string,
): { nodes: BehaviorNode[]; connections: BehaviorConnection[] } {
  if (isProtectedKeyframePortId(portId)) {
    return { nodes, connections };
  }

  const baseId = portId.endsWith("Out") ? portId.replace(/Out$/, "") : portId;
  if (isProtectedKeyframePortId(baseId)) {
    return { nodes, connections };
  }

  const removeIds = new Set([baseId, `${baseId}Out`]);

  const nextNodes = nodes.map((node) => {
    if (node.type !== "action" || node.kind !== "animKeyframe") return node;
    return {
      ...node,
      ports: node.ports.filter((port) => !removeIds.has(port.id)),
    };
  });

  const nextConnections = connections.filter(
    (connection) =>
      !removeIds.has(connection.fromPortId) &&
      !removeIds.has(connection.toPortId),
  );

  return { nodes: nextNodes, connections: nextConnections };
}

/**
 * Detects which animatable properties differ across captured keyframe snapshots.
 *
 * A property is kept when at least two keyframes disagree on its numeric value.
 */
export function detectDifferingAnimatableProperties(
  snapshots: ReadonlyArray<Partial<Record<AnimatableProperty, number>>>,
): AnimatableProperty[] {
  if (snapshots.length === 0) return [];

  return ANIMATABLE_PROPERTIES.filter((prop) => {
    const values = snapshots.map((snap) => {
      const raw = snap[prop];
      if (typeof raw === "number" && Number.isFinite(raw)) return raw;
      return DEFAULT_ANIMATABLE_VALUES[prop];
    });
    const first = values[0]!;
    return values.some((value) => value !== first);
  });
}

/**
 * True when the behavior node exposes an input port for the given property.
 * Used by compilation / runtime to skip absent fields.
 */
export function animKeyframeHasPropertyPort(
  node: BehaviorNode,
  prop: AnimatableProperty,
): boolean {
  if (node.type !== "action" || node.kind !== "animKeyframe") return false;
  return node.ports.some(
    (port) => port.id === prop && port.direction === "input",
  );
}
